import {IAsyncUploaderFileMeta} from '@src/@interfaces/services/Http/IAsyncUploaderFileMeta';
import {Events, IAsyncUploader} from '@src/@interfaces/services/Http/IAsyncUploader';
import {IListener, ISubscription} from '@src/@interfaces/services/Events';
import {IAsyncUploaderQueuedFile} from '@src/@interfaces/services/Http/IAsyncUploaderQueuedFile';

export default class AsyncUploaderQueuedFile implements IAsyncUploaderQueuedFile {
    constructor(
        protected uploader: IAsyncUploader,
        protected meta: IAsyncUploaderFileMeta
    ) {
    }

    get<K extends keyof IAsyncUploaderFileMeta>(key: K): IAsyncUploaderFileMeta[K] | null {
        return this.meta[key] ?? null;
    }

    getFile(): File | null {
        return this.uploader.getFile(this.getMeta().uniqueId);
    }

    getMeta(): IAsyncUploaderFileMeta {
        return this.meta;
    }

    getUploader(): IAsyncUploader {
        return this.uploader;
    }

    remove(): boolean {
        return this.uploader.remove(this.getMeta().uniqueId);
    }

    pause(): boolean {
        return this.uploader.pause(this.getMeta().uniqueId);
    }

    resume(): boolean {
        return this.uploader.resume(this.getMeta().uniqueId);
    }

    abort(): boolean {
        return this.uploader.abort(this.getMeta().uniqueId);
    }

    getProgress(): number {
        return this.uploader.getProgressOfItem(this.getMeta().uniqueId);
    }

    listen<TPayload extends {
        uniqueId: string | null
    }>(event: Events | Events[], listener: IListener<TPayload>): ISubscription {
        return this.uploader.listen<TPayload>(event, (event, payload) => {
            if (payload.uniqueId === this.getMeta().uniqueId) {
                return listener(event, payload);
            }
        });
    }

    onError(listener: IListener<{ uniqueId: string | null, error: Error }>): ISubscription {
        return this.listen<{ uniqueId: string | null, error: Error }>(Events.ERROR_OCCURRED, listener);
    }

    onPause(listener: IListener<{ uniqueId: string }>): ISubscription {
        return this.listen(Events.PAUSE, listener);
    }

    onResume(listener: IListener<{ uniqueId: string }>): ISubscription {
        return this.listen(Events.RESUME, listener);
    }

    onAbort(listener: IListener<{ uniqueId: string }>): ISubscription {
        return this.listen(Events.ABORT, listener);
    }

    onRemove(listener: IListener<{ uniqueId: string }>): ISubscription {
        return this.listen([Events.REMOVE, Events.FILE_REMOVED_FROM_LIST], listener);
    }

    onSend(listener: IListener<{ uniqueId: string }>): ISubscription {
        return this.listen(Events.SEND, listener);
    }

    onProgress(listener: IListener<{ uniqueId: string, event: ProgressEvent }>): ISubscription {
        return this.listen(Events.PROGRESS, listener);
    }

    onSuccess(listener: IListener<{ uniqueId: string }>): ISubscription {
        return this.listen(Events.SUCCESS, listener);
    }

    onFail(listener: IListener<{ uniqueId: string }>): ISubscription {
        return this.listen(Events.FAIL, listener);
    }

    onComplete(listener: IListener<{ uniqueId: string }>): ISubscription {
        return this.listen(Events.COMPLETE, listener);
    }
}
